/**
 * errorReporting.ts — M10: Frontend global error capture
 *
 * Captures:
 *   - Unhandled JS exceptions (window.onerror)
 *   - Unhandled promise rejections (window.onunhandledrejection)
 *   - React Error Boundary errors (reportReactError)
 *
 * Sends structured events to Firebase Analytics (web Crashlytics equivalent).
 * Deduplicates identical errors within a 5-second window to avoid floods.
 */

import { trackEvent } from './analytics';
import { logClient } from './obsClientLogger';

const DEDUP_WINDOW_MS = 5_000;
const recentErrors = new Map<string, number>();

function shouldReport(key: string): boolean {
  const last = recentErrors.get(key);
  const now = Date.now();
  if (last && now - last < DEDUP_WINDOW_MS) return false;
  recentErrors.set(key, now);
  // Prune stale entries
  if (recentErrors.size > 50) {
    const cutoff = now - DEDUP_WINDOW_MS * 10;
    for (const [k, ts] of recentErrors) {
      if (ts < cutoff) recentErrors.delete(k);
    }
  }
  return true;
}

function safeStringify(o: unknown): string {
  try {
    const s = JSON.stringify(o, (_k, v) =>
      v instanceof Error ? `${v.name}: ${v.message}` : v,
    );
    if (s && s !== '{}') return s;
  } catch {
    /* referencias cíclicas, getters que lanzan, etc. */
  }
  return Object.prototype.toString.call(o);
}

/** Describe un Event del DOM (rejections cuyo `reason` es un Event: fallo de
 *  carga de recurso, error de media, cierre de websocket, etc.). Antes esto
 *  producía "[object Event]" sin ningún detalle útil. */
function describeEvent(ev: Event): string {
  if (typeof ErrorEvent !== 'undefined' && ev instanceof ErrorEvent) {
    const inner = ev.error instanceof Error ? ` (${ev.error.name}: ${ev.error.message})` : '';
    const at = ev.filename ? ` @ ${ev.filename}:${ev.lineno ?? ''}` : '';
    return `ErrorEvent: ${ev.message || ''}${at}${inner}`;
  }
  const parts: string[] = [`Event(type=${ev.type})`];
  const t = ev.target as
    | (EventTarget & {
        tagName?: string;
        nodeName?: string;
        src?: string;
        currentSrc?: string;
        href?: string;
        url?: string;
        readyState?: number;
        error?: { code?: number; message?: string } | null;
      })
    | null;
  if (t) {
    const tag = t.tagName || t.nodeName || t.constructor?.name || '';
    if (tag) parts.push(`target=${String(tag).toLowerCase()}`);
    const url = t.currentSrc || t.src || t.href || t.url || '';
    if (url) parts.push(`src=${String(url).slice(0, 140)}`);
    if (t.error && (t.error.code || t.error.message)) {
      parts.push(`mediaError=${t.error.code ?? ''}${t.error.message ? ':' + t.error.message : ''}`);
    }
    if (typeof t.readyState === 'number') parts.push(`readyState=${t.readyState}`);
  }
  return parts.join(' ');
}

function formatError(error: unknown): { message: string; stack: string } {
  // Error y subclases (incl. muchas DOMException): mensaje + stack completos.
  if (error instanceof Error) {
    const name = error.name && error.name !== 'Error' ? `${error.name}: ` : '';
    return {
      message: (`${name}${error.message}`.trim() || error.name || 'Error').slice(0, 200),
      stack: (error.stack ?? '').slice(0, 2000),
    };
  }
  // Eventos del DOM: extraer tipo, target, url, error de media, etc.
  if (typeof Event !== 'undefined' && error instanceof Event) {
    return { message: describeEvent(error).slice(0, 250), stack: '' };
  }
  // Objeto plano (ej. errores de Firebase {code,message}): campos comunes o JSON.
  if (error && typeof error === 'object') {
    const o = error as Record<string, unknown>;
    const msg =
      (typeof o.message === 'string' && o.message) ||
      (typeof o.reason === 'string' && o.reason) ||
      (typeof o.code === 'string' && o.code) ||
      safeStringify(o);
    return {
      message: String(msg).slice(0, 200),
      stack: typeof o.stack === 'string' ? o.stack.slice(0, 2000) : '',
    };
  }
  return { message: (String(error) || 'unknown').slice(0, 200), stack: '' };
}

function report(
  type: 'js_error' | 'promise_rejection' | 'react_error',
  message: string,
  stack: string,
  extra?: Record<string, string>,
): void {
  const key = `${type}:${message}`;
  if (!shouldReport(key)) return;

  void trackEvent('app_error', {
    error_type: type,
    error_message: message,
    stack_trace: stack.slice(0, 200),
    ...extra,
  });

  // OBS: enviar también al visor de eventos (obs_events vía portal_ingestLogs).
  logClient({
    nivel: 'ERROR',
    categoria: 'SISTEMA',
    codigo: `SISTEMA_ERROR_FRONTEND_${type}`.toUpperCase(),
    mensaje: message,
    componente: extra?.source || extra?.context || undefined,
    error: { tipo: type, mensaje: message, stack },
    detalle: extra,
  });

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[errorReporting] ${type}:`, message);
  }
}

// ── Global handlers ───────────────────────────────────────────────────────────

let installed = false;

export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const { message, stack } = formatError(event.error ?? event.message);
    report('js_error', message, stack, {
      source: event.filename ?? '',
      lineno: String(event.lineno ?? ''),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const { message, stack } = formatError(event.reason);
    report('promise_rejection', message, stack);
  });
}

// ── React Error Boundary helper ───────────────────────────────────────────────

export function reportReactError(error: Error, componentStack: string): void {
  report('react_error', error.message.slice(0, 150), error.stack?.slice(0, 300) ?? '', {
    component_stack: componentStack.slice(0, 200),
  });
}

// ── Manual error reporting (for try/catch blocks) ─────────────────────────────

export function reportError(context: string, error: unknown): void {
  const { message, stack } = formatError(error);
  report('js_error', message, stack, { context: context.slice(0, 80) });
}
